import { useEffect } from "react";
import { getUserSignUpType } from "@/features/authentication";
import { useGetCurrentUserQuery } from "@/shared/services";
import { setSentryUser } from "@/shared/utils";
import { useAnalyticsStoreShallow } from "../store/analytics.store";
import {
  handleMixpanelAuth,
  incrementor,
  register,
  setPeople,
  shouldDisableTrack,
} from "../utils";

export const useRegisterUserProperties = (authenticated = false) => {
  const { data: user } = useGetCurrentUserQuery();
  const { setBlocked, setInitializedType } = useAnalyticsStoreShallow((s) => ({
    setBlocked: s.setBlocked,
    setInitializedType: s.setInitializedType,
  }));

  useEffect(() => {
    if (!authenticated || !user) return;
    const uid = user.oktaId;
    const identifier = user.distinctId || uid;
    if (shouldDisableTrack(user.email)) {
      setBlocked(true);
      return;
    }

    handleMixpanelAuth(identifier);

    setPeople({
      $email: user.email,
      Name: user.firstName,
      orgId: user.orgId,
    });

    setSentryUser({
      _id: user._id,
      email: user.email,
      orgId: user.orgId,
    });

    register({
      "user id": user.oktaId,
      "user name": user.firstName,
      "user email": user.email,
      "user created": user.createdAt,
      "user sign up type": getUserSignUpType(),
      "visit count": incrementor("visit count"),
      "browser window width": window.innerWidth,
      "browser window height": window.innerHeight,
      "is mobile": window.innerWidth < 1200,
      "user has profile image": user.profile?.picture,
      "user has calendar link": user.profile?.calendar,
      "user profile role": user.profile?.role,
      "user first analytics": user.firstAnalytics,
      "autosave counter": user.autosaveCount ?? 0,
      "engagement level": getEngagementLevel(user.autosaveCount ?? 0),
      "walkthrough skipped": !!user.walkthrough?.hasSkipped,
      "walkthrough last step": (user.walkthrough?.lastStepIndex || 0) + 1,
      "last app visit": new Date().toISOString(),
    });
    setInitializedType("user");
  }, [authenticated, user, setBlocked, setInitializedType]);
};

const getEngagementLevel = (editCount: number): string | null => {
  const levels = [
    { threshold: 10, label: "less than 10 edits" },
    { threshold: 50, label: "10-50 edits" },
    { threshold: 100, label: "50-100 edits" },
    { threshold: 500, label: "100-500 edits" },
    { threshold: Number.POSITIVE_INFINITY, label: "500+ edits" },
  ];

  const level = levels.find((level) => editCount < level.threshold);

  return level ? level.label : null;
};
